/**
 * Cursor feedback for hovered drawings.
 *
 * Pure mapping, driven from the overlay store's hover state by ChartManager —
 * NOT from per-template `onMouseEnter`. Most drawings are klinecharts built-ins
 * (`straightLine`, `rayLine`, `rect`, …) whose templates we don't own, so a
 * per-template hook would only ever cover our own overlays.
 */
import {
  FIG_BODY,
  ANCHOR_ENTRY_LEFT,
  ANCHOR_ENTRY_RIGHT,
  POSITION_TOOL_NAMES,
} from './riskRewardOverlay'
import { RANGE_TOOL_NAMES, FIG_RANGE_EDGE } from './rangeOverlays'

export type PositionCursor = 'move' | 'ns-resize' | 'ew-resize' | 'default' | null

/** klinecharts EventOverlayInfoFigureType.Point */
const FIGURE_TYPE_POINT = 1

/** klinecharts' own grab handles. */
const POINT_KEY = /^overlay_figure_point_(\d+)$/
/** Our anchor-keyed custom handles (patched klinecharts routes these to a
 *  point drag). See riskRewardOverlay FIG_ANCHOR. */
// Prefix match, mirroring the vendored patch: composite keys like
// `anchor_2__weight_1` (a plan label that drags its own line but clicks as a
// menu target) still read as anchors here.
const ANCHOR_KEY = /^anchor_(\d+)/

/**
 * Cursor for the figure currently under the pointer.
 *
 * Position tools get direction-specific cursors because their anchors are
 * CONSTRAINED — the entry ends only set width, target/stop only set price.
 * Every other drawing's handles move freely in both axes, so `move` (the
 * four-arrow) is the honest signal there.
 */
export function drawingCursorFor(
  overlayName: string | undefined,
  figureKey: string | undefined,
  figureType: number,
): PositionCursor {
  const isPosition =
    overlayName !== undefined && (POSITION_TOOL_NAMES as readonly string[]).includes(overlayName)

  if (isPosition) {
    const anchor =
      figureKey !== undefined ? (ANCHOR_KEY.exec(figureKey) ?? POINT_KEY.exec(figureKey)) : null
    if (anchor) {
      // Anchor order: 0/1 entry ends (set WIDTH), 2/3 target/stop (set PRICE).
      const idx = Number(anchor[1])
      return idx === ANCHOR_ENTRY_LEFT || idx === ANCHOR_ENTRY_RIGHT ? 'ew-resize' : 'ns-resize'
    }
    if (figureKey === FIG_BODY) return 'move'
  }

  // Range tools do TWO different things, and the cursor has to say which:
  //
  //   the EDGE  → translates the whole range      → `move` (four arrows)
  //   a grab DOT → resizes along the tool's axis   → directional
  //
  // Showing the same cursor for both made two quite different drags look
  // identical until you tried them.
  const isRange = Object.values(RANGE_TOOL_NAMES).includes(overlayName ?? '')
  if (isRange) {
    if (figureKey === FIG_RANGE_EDGE) return 'move'
    if (figureType === FIGURE_TYPE_POINT) {
      // Date & Price resizes a CORNER — `eventPressedPointMove` sets dataIndex
      // and value together — so a single-axis cursor there would be a lie the
      // drag immediately breaks.
      if (overlayName === RANGE_TOOL_NAMES.date) return 'ew-resize'
      if (overlayName === RANGE_TOOL_NAMES.price) return 'ns-resize'
      return 'move'
    }
  }

  // Any other drawing: a grab handle or its body, both freely movable.
  if (figureType === FIGURE_TYPE_POINT) return 'move'
  return 'move'
}

/**
 * True when a press is on a drawing's POINT (a klinecharts grab handle or one of
 * our anchor-keyed handles) rather than its body. A point drag re-aims one
 * anchor; a body drag translates the whole drawing.
 */
export function isPointDrag(figureKey: string | undefined, figureType: number): boolean {
  if (figureType === FIGURE_TYPE_POINT) return true
  return figureKey !== undefined && ANCHOR_KEY.test(figureKey)
}

/**
 * Cursor to apply for the current hover.
 *
 * While actively drawing, the standard arrow wins: a tool is armed (placing
 * points, e.g. Shift arming the measure/ruler) or a point of a finished drawing
 * is being dragged. The arrow's tip is the exact spot the point lands, which the
 * four-arrow `move` hides. (The in-progress overlay follows the pointer and
 * reads as "hovered", so without this gate the cursor would flip to `move` the
 * moment a tool activates.) Only when idle does a hovered figure get its drag
 * cursor.
 */
export function hoverCursor(
  armed: boolean,
  overlayName: string | undefined,
  figureKey: string | undefined,
  figureType: number,
  draggingPoint = false,
): PositionCursor {
  if (armed || draggingPoint) return 'default'
  // figureType 0 = EventOverlayInfoFigureType.None → not over a figure.
  if (figureType === 0) return null
  return drawingCursorFor(overlayName, figureKey, figureType)
}
